import { ContactDetailsInfo, Patient } from '@store/patientStore';
import { AppointmentBody, FormConfigurationDTO } from '@utils/interfaces';
import { mergeCustomFields } from '@utils/functions';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import {
  getMarketingConsentDefaultValues,
  getMarketingConsentFields,
} from '@components/MarketingConsentForm/MarketingConsentForm.utils';

interface BuildAppointmentBodyForSaveArgs {
  practiceId: string;
  currentLocationId: string;
  patients: Array<Patient | undefined>;
  contactsDetails: ContactDetailsInfo;
  formConfigs: FormConfigurationDTO;
  integrationMode?: string;
  isFromRescheduleFlow: boolean;
}

/**
 * Cleans insurance details before saving the appointment.
 * `provideInsuranceDetails` is FE-only state: it drives form visibility and
 * validation, but it must not be sent to the BFF.
 * When the user answers "no", we keep carrier/member/plan data and remove
 * only the holder personal details.
 */
const sanitizeInsuranceDetailsForAppointment = <
  T extends {
    provideInsuranceDetails?: string;
    isHolder?: string;
    firstName?: string;
    lastName?: string;
    gender?: string;
    dateOfBirth?: string;
    phone?: unknown;
    streetAddress?: string;
    city?: string;
    state?: string;
    country?: string;
    zipCode?: string;
    relationship?: string;
    sameAsForPrimaryInsurance?: boolean;
  },
>(
  details?: T,
): T | undefined => {
  if (!details) return undefined;

  const { provideInsuranceDetails, ...detailsToSend } = details;

  if (provideInsuranceDetails !== StaticValues.NO) {
    return detailsToSend as T;
  }

  const baseInsuranceDetails = { ...detailsToSend };
  delete baseInsuranceDetails.isHolder;
  delete baseInsuranceDetails.firstName;
  delete baseInsuranceDetails.lastName;
  delete baseInsuranceDetails.gender;
  delete baseInsuranceDetails.dateOfBirth;
  delete baseInsuranceDetails.phone;
  delete baseInsuranceDetails.streetAddress;
  delete baseInsuranceDetails.city;
  delete baseInsuranceDetails.state;
  delete baseInsuranceDetails.country;
  delete baseInsuranceDetails.zipCode;
  delete baseInsuranceDetails.relationship;
  delete baseInsuranceDetails.sameAsForPrimaryInsurance;

  return baseInsuranceDetails as T;
};

/**
 * Prepares a patient copy that is safe for the final payload.
 * Sanitization must touch both `patientDetails.*InsuranceDetails` and
 * `insurance.holder`, because save appointment reads both branches:
 * details feed the patientDetails section, while `insurance` feeds the final
 * insurance node.
 */
export const sanitizePatientForAppointment = (patient: Patient): Patient => {
  const primaryInsuranceDetails =
    patient.patientDetails?.primaryInsuranceDetails;
  const secondaryInsuranceDetails =
    patient.patientDetails?.secondaryInsuranceDetails;
  const shouldSendPrimaryHolder =
    primaryInsuranceDetails?.provideInsuranceDetails !== StaticValues.NO;
  const shouldSendSecondaryHolder =
    secondaryInsuranceDetails?.provideInsuranceDetails !== StaticValues.NO;

  return {
    ...patient,
    insurance: {
      ...patient.insurance,
      holder: shouldSendPrimaryHolder ? patient.insurance?.holder : undefined,
      secondaryInsurance: patient.insurance?.secondaryInsurance
        ? {
            ...patient.insurance.secondaryInsurance,
            holder: shouldSendSecondaryHolder
              ? patient.insurance.secondaryInsurance.holder
              : undefined,
          }
        : undefined,
    },
    patientDetails: {
      ...patient.patientDetails,
      primaryInsuranceDetails: sanitizeInsuranceDetailsForAppointment(
        primaryInsuranceDetails,
      ),
      secondaryInsuranceDetails: sanitizeInsuranceDetailsForAppointment(
        secondaryInsuranceDetails,
      ),
    },
  };
};

/**
 * Builds the save appointment body in one place.
 * ThankYouPage remains responsible for when to save; this utility owns how the
 * payload is assembled: it filters real patients, sanitizes insurance, clears
 * expired reservations, rebuilds custom fields from form config, and applies
 * marketing consent defaults. In reschedule flows, marketing consents are
 * intentionally emptied so they are not sent back to the BFF.
 */
export const buildAppointmentBodyForSave = ({
  practiceId,
  currentLocationId,
  patients,
  contactsDetails,
  formConfigs,
  integrationMode,
  isFromRescheduleFlow,
}: BuildAppointmentBodyForSaveArgs): AppointmentBody => {
  const sanitizedPatients = patients
    .filter((patient): patient is Patient => Boolean(patient))
    .map(sanitizePatientForAppointment);

  const body: AppointmentBody = {
    practiceId,
    currentLocationId,
    patients: sanitizedPatients,
    contactsDetails: { ...contactsDetails },
  };

  sanitizedPatients.forEach(patient => {
    if (patient.reservationId && patient.extraInfoScheduleExam?.expired) {
      patient.reservationId = undefined;
    }

    if (
      formConfigs.patientDetails.customFieldsPatient?.show &&
      patient.patientDetails?.storeCustomFieldsPatient &&
      formConfigs.patientDetails.customFieldsPatient.children.length > 0
    ) {
      patient.patientDetails.customFieldsPatient = mergeCustomFields(
        patient.patientDetails.storeCustomFieldsPatient,
        formConfigs.patientDetails.customFieldsPatient.children,
      );
    }

    if (
      formConfigs.patientDetails.customFieldsInsurance?.show &&
      patient.patientDetails?.storeCustomFieldsInsurance &&
      formConfigs.patientDetails.customFieldsInsurance.children.length > 0
    ) {
      patient.patientDetails.customFieldsInsurance = mergeCustomFields(
        patient.patientDetails.storeCustomFieldsInsurance,
        formConfigs.patientDetails.customFieldsInsurance.children,
      );
    }

    if (formConfigs.customPage?.show && patient.storeCustomFieldsCustomPage) {
      patient.customFieldsCustomPage = mergeCustomFields(
        patient.storeCustomFieldsCustomPage,
        formConfigs.customPage.children,
      );
    }
  });

  if (
    formConfigs.customFieldsMarketing.children.some(
      field => field.status !== StaticValues.FIELD_STATUS_HIDDEN,
    )
  ) {
    const marketingFields = getMarketingConsentFields(
      formConfigs.customFieldsMarketing,
      integrationMode,
    );
    const storeCustomFieldsMarketing = getMarketingConsentDefaultValues(
      marketingFields,
      body.contactsDetails,
    ).contactsDetails.storeCustomFieldsMarketing;

    body.contactsDetails.customFieldsMarketing = isFromRescheduleFlow
      ? []
      : mergeCustomFields(
          storeCustomFieldsMarketing,
          formConfigs.customFieldsMarketing.children,
        );
  }

  return body;
};
